import { DatePipe, DecimalPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule } from 'lucide-angular';
import { DatePickerModule } from 'primeng/datepicker';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { GlobalOverlayService } from '../../../core/ui/global-overlay.service';
import { CashFlowExportService } from '../services/cash-flow-export.service';
import {
  CashFlowCategory,
  CashFlowDirection,
  CashFlowItem,
  CashFlowPaymentMethod,
  CashFlowService,
} from '../services/cash-flow.service';

const currentMonth = (() => {
  const today = new Date();
  return {
    from: new Date(today.getFullYear(), today.getMonth(), 1),
    to: new Date(today.getFullYear(), today.getMonth() + 1, 0),
  };
})();

function formatDateFilter(date: Date | null): string {
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

@Component({
  selector: 'app-cash-flow-page',
  imports: [
    DatePipe,
    DecimalPipe,
    FormsModule,
    LucideAngularModule,
    DatePickerModule,
    InputTextModule,
    SelectModule,
    TableModule,
  ],
  templateUrl: './cash-flow-page.component.html',
})
export class CashFlowPageComponent implements OnInit {
  private readonly overlay = inject(GlobalOverlayService);
  private readonly cashFlowService = inject(CashFlowService);
  private readonly cashFlowExportService = inject(CashFlowExportService);

  readonly items = signal<CashFlowItem[]>([]);
  readonly direction = signal<'ALL' | CashFlowDirection>('ALL');
  readonly category = signal<'ALL' | CashFlowCategory>('ALL');
  readonly paymentMethod = signal<'ALL' | CashFlowPaymentMethod>('ALL');
  readonly searchTerm = signal('');
  readonly dateFrom = signal(formatDateFilter(currentMonth.from));
  readonly dateTo = signal(formatDateFilter(currentMonth.to));
  readonly pendingDirection = signal<'ALL' | CashFlowDirection>('ALL');
  readonly pendingCategory = signal<'ALL' | CashFlowCategory>('ALL');
  readonly pendingPaymentMethod = signal<'ALL' | CashFlowPaymentMethod>('ALL');
  readonly pendingDateFrom = signal<Date | null>(new Date(currentMonth.from));
  readonly pendingDateTo = signal<Date | null>(new Date(currentMonth.to));

  readonly directionOptions: Array<{ label: string; value: 'ALL' | CashFlowDirection }> = [
    { label: 'ทั้งหมด', value: 'ALL' },
    { label: 'รายรับ', value: 'INCOME' },
    { label: 'รายจ่าย', value: 'EXPENSE' },
  ];

  readonly categoryOptions: Array<{ label: string; value: 'ALL' | CashFlowCategory }> = [
    { label: 'ทุกประเภท', value: 'ALL' },
    { label: 'รับชำระลูกค้า', value: 'CUSTOMER_PAYMENT' },
    { label: 'ค่าใช้จ่าย', value: 'EXPENSE' },
    { label: 'คอมมิชชั่น', value: 'COMMISSION' },
    { label: 'คืนเงินลูกค้า', value: 'REFUND' },
  ];

  readonly paymentMethodOptions: Array<{
    label: string;
    value: 'ALL' | CashFlowPaymentMethod;
  }> = [
    { label: 'ทุกช่องทาง', value: 'ALL' },
    { label: 'โอนผ่านธนาคาร', value: 'BANK_TRANSFER' },
    { label: 'เงินสด', value: 'CASH' },
    { label: 'บัตรเครดิต', value: 'CREDIT_CARD' },
  ];

  readonly filteredItems = computed(() => {
    const query = this.searchTerm().trim().toLocaleLowerCase('th');
    if (!query) return this.items();
    return this.items().filter((item) =>
      [item.transactionNo, item.bookingNo, item.customerName, item.description]
        .join(' ')
        .toLocaleLowerCase('th')
        .includes(query),
    );
  });

  readonly incomeTotal = computed(() =>
    this.filteredItems()
      .filter((item) => item.direction === 'INCOME')
      .reduce((total, item) => total + item.amount, 0),
  );

  readonly expenseTotal = computed(() =>
    this.filteredItems()
      .filter((item) => item.direction === 'EXPENSE')
      .reduce((total, item) => total + item.amount, 0),
  );

  ngOnInit(): void {
    void this.loadItems();
  }

  async applyFilters(): Promise<void> {
    this.direction.set(this.pendingDirection());
    this.category.set(this.pendingCategory());
    this.paymentMethod.set(this.pendingPaymentMethod());
    this.dateFrom.set(formatDateFilter(this.pendingDateFrom()));
    this.dateTo.set(formatDateFilter(this.pendingDateTo()));
    await this.loadItems();
  }

  async resetFilters(): Promise<void> {
    this.searchTerm.set('');
    this.pendingDirection.set('ALL');
    this.pendingCategory.set('ALL');
    this.pendingPaymentMethod.set('ALL');
    this.pendingDateFrom.set(new Date(currentMonth.from));
    this.pendingDateTo.set(new Date(currentMonth.to));
    this.direction.set('ALL');
    this.category.set('ALL');
    this.paymentMethod.set('ALL');
    this.dateFrom.set(formatDateFilter(currentMonth.from));
    this.dateTo.set(formatDateFilter(currentMonth.to));
    await this.loadItems();
  }

  categoryLabel(category: CashFlowCategory): string {
    return this.categoryOptions.find((option) => option.value === category)?.label ?? category;
  }

  paymentMethodLabel(item: CashFlowItem): string {
    const label = this.paymentMethodOptions.find(
      (option) => option.value === item.paymentMethod,
    )?.label ?? item.paymentMethod;
    return item.paymentMethod === 'CREDIT_CARD'
      ? `${label} ${item.creditCardFeePercent}%`
      : label;
  }

  creditCardFeeResponsibilityLabel(responsibility: 'CUSTOMER' | 'COMPANY' | null): string {
    if (responsibility === 'CUSTOMER') return 'ลูกค้าชำระ';
    if (responsibility === 'COMPANY') return 'บริษัทรับภาระ';
    return 'ไม่ระบุผู้รับภาระ';
  }

  openDocument(fileUrl: string | null): void {
    if (!fileUrl) return;
    window.open(this.cashFlowService.resolveFileUrl(fileUrl), '_blank', 'noopener,noreferrer');
  }

  async exportExcel(): Promise<void> {
    if (this.filteredItems().length === 0) {
      this.overlay.error('ไม่สามารถ Export ได้', 'ไม่พบรายการตามตัวกรองปัจจุบัน');
      return;
    }
    this.overlay.showLoading('กำลังสร้างไฟล์ Excel...');
    try {
      await this.cashFlowExportService.export(this.filteredItems(), {
        dateFrom: this.dateFrom(),
        dateTo: this.dateTo(),
        direction: this.optionLabel(this.directionOptions, this.direction()),
        category: this.optionLabel(this.categoryOptions, this.category()),
        paymentMethod: this.optionLabel(this.paymentMethodOptions, this.paymentMethod()),
        searchTerm: this.searchTerm().trim(),
      });
    } catch {
      this.overlay.error('สร้างไฟล์ Excel ไม่สำเร็จ', 'กรุณาลองใหม่อีกครั้ง');
    } finally {
      this.overlay.hideLoading();
    }
  }

  private async loadItems(): Promise<void> {
    this.overlay.showLoading('กำลังโหลดรายการรับ–จ่าย...');
    try {
      this.items.set(
        await this.cashFlowService.getItems({
          dateFrom: this.dateFrom(),
          dateTo: this.dateTo(),
          direction: this.direction(),
          category: this.category(),
          paymentMethod: this.paymentMethod(),
        }),
      );
    } catch (error: unknown) {
      const message =
        error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
          ? error.error.message
          : 'กรุณาลองใหม่อีกครั้ง';
      this.overlay.error('โหลดรายการรับ–จ่ายไม่สำเร็จ', message);
    } finally {
      this.overlay.hideLoading();
    }
  }

  private optionLabel<T extends string>(
    options: Array<{ label: string; value: T }>,
    value: T,
  ): string {
    return options.find((option) => option.value === value)?.label ?? value;
  }

}
